import React, { useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'

import { ILayoutItem, TDashboard } from 'types/GlobalDashboard'

import { useEditMode } from 'redux/hooks/DashboardPage'

import { useCustomer } from 'redux/hooks/Customer'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'
import { useIsCreatingWidget } from 'redux/hooks/Widgets'
import { getMetricTitle } from 'utils/metric'

interface EditModeBarProps {
  availableSpace: ILayoutItem | false
  dashboard: TDashboard
  sx?: object
}

const EditModeBar = (props: EditModeBarProps) => {
  const { availableSpace, dashboard, sx } = props

  const { t, lang, translateEnum } = useTranslation()
  const { tryCreateDashboardWidget } = useDashboardWidgetActions()
  const customer = useCustomer()
  const { loading: accountLoading } = useAccountSettings()
  const isCreatingWidget = useIsCreatingWidget()
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false)

  const editMode = useEditMode()

  // loading
  if (accountLoading || !customer) {
    return null
  }

  // success
  return (
    <>
      <Collapse in={editMode}>
        <ThemeProvider theme={getTheme('dark')}>
          <Box
            sx={{
              bgcolor: 'background.default',
              py: 0.5,
              pl: 2,
              pr: 4,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ...sx
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={t('dashboards', 'editing_mode_limitations')}>
                <Icon
                  color="primary"
                  name="Info"
                  fontSize="small"
                  sx={{ mr: 1 }}
                />
              </Tooltip>
              <Text color="text.primary">
                {t('dashboards', 'editing_mode_active')}
              </Text>
            </Box>
            <Tooltip
              title={
                Boolean(!availableSpace)
                  ? t('dashboards', 'dashboard_full')
                  : ''
              }
            >
              <span>
                <Button
                  disabled={!availableSpace}
                  startIcon={<Icon name="AddOutlined" />}
                  onClick={() => setKpiDialogOpen(true)}
                  size="small"
                  variant="text"
                >
                  {t('widgets', 'add_widget')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </ThemeProvider>
      </Collapse>

      <MetricsDialog
        availableSteps={['variant', 'slices']}
        creating={isCreatingWidget}
        handleAddOther={(type) => {
          if (dashboard.available_space) {
            tryCreateDashboardWidget(
              {
                dashboard_id: dashboard.id,
                kpi_id: null,
                type,
                width: dashboard.available_space.w,
                height: dashboard.available_space.h,
                x: dashboard.available_space.x,
                y: dashboard.available_space.y
              },
              (err) => {
                if (!err) setKpiDialogOpen(false)
              }
            )
          }
        }}
        handleOnAdd={(metric, settings) => {
          if (dashboard.available_space) {
            const title = getMetricTitle(metric, settings, lang, translateEnum)

            tryCreateDashboardWidget(
              {
                dashboard_id: dashboard.id,
                kpi_id: metric.id,
                width: dashboard.available_space.w,
                height: dashboard.available_space.h,
                x: dashboard.available_space.x,
                y: dashboard.available_space.y,
                ...settings,
                title
              },
              (err) => {
                if (!err) setKpiDialogOpen(false)
              }
            )
          }
        }}
        open={kpiDialogOpen}
        onClose={() => setKpiDialogOpen(false)}
        title={t('widgets', 'add_widget')}
        variant="dashboards"
      />
    </>
  )
}

export default EditModeBar
